import { useSlidingIndicator } from '@/hooks/useSlidingIndicator';

export interface TabItem {
  id: string;
  label: string;
  icon?: string;
  count?: number;
}

interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}

export default function Tabs({ items, value, onChange, className = '' }: TabsProps) {
  // One burgundy pill glides to the chosen tab.
  const pill = useSlidingIndicator<HTMLDivElement>('[data-active="true"]', `${value}|${items.length}`);
  return (
    <div
      ref={pill.ref}
      className={`relative inline-flex flex-wrap items-center gap-1 rounded-full border border-background-300 bg-background-200/60 p-1 ${className}`}
    >
      {pill.style ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0 rounded-full bg-primary-500 shadow-[0_6px_16px_-6px] shadow-primary-500/70"
          style={pill.style}
        />
      ) : null}
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            data-active={active}
            onClick={() => onChange(item.id)}
            className={`group relative inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-label font-medium whitespace-nowrap transition-colors duration-300 cursor-pointer active:scale-[0.97] ${
              active
                ? 'text-background-50 font-semibold'
                : 'text-foreground-600 hover:bg-background-50 hover:text-foreground-900'
            }`}
          >
            {item.icon ? (
              <span
                className={`w-4 h-4 flex items-center justify-center text-base transition-transform duration-300 ${
                  active ? 'scale-110' : 'group-hover:-rotate-6 group-hover:scale-110 motion-reduce:transform-none'
                }`}
              >
                <i className={item.icon} />
              </span>
            ) : null}
            {item.label}
            {typeof item.count === 'number' ? (
              <span
                className={`rounded-full px-1.5 text-[11px] ${
                  active ? 'bg-background-50/25 text-background-50' : 'bg-background-300 text-foreground-700'
                }`}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}